import { membershipQueries } from "@app/config/queryFactory";
import { myinfoQueries } from "@features/myinfo/api/queryFactory";
import {
  mdiAccountOutline,
  mdiBadgeAccountHorizontalOutline,
  mdiCalendarMonthOutline,
  mdiDomain,
  mdiEmailOutline,
  mdiPhoneOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

const displayValue = (value?: Nullable<string>): string => {
  return value || "-";
};

const formatDate = (dateStr: string): string => {
  return dayjs(dateStr).format("MMM DD, YYYY");
};

export function Profile() {
  const { t } = useTranslation();

  const { data: profile } = useQuery(myinfoQueries.profile());
  const { data: membership, isError: isMembershipError } = useQuery(membershipQueries.membership());

  const hasMembership = !isMembershipError && membership;

  return (
    <div>
      <dl className="grid grid-cols-2 gap-6 mb-6 sm:gap-8 sm:mb-10">
        <div className="flex col-span-2 gap-x-3 items-center sm:col-span-1">
          <div className="bg-gray-10 p-[10px] rounded-2">
            <Icon path={mdiAccountOutline} size="20px" className="text-gray-800 shrink-0" />
          </div>
          <div className="flex flex-col gap-y-1">
            <ProfileTitle>{t("representative")}</ProfileTitle>
            <ProfileContent>{displayValue(profile?.name)}</ProfileContent>
          </div>
        </div>

        <div className="flex col-span-2 gap-x-3 items-center sm:col-span-1">
          <div className="bg-gray-10 p-[10px] rounded-2">
            <Icon path={mdiDomain} size="20px" className="text-gray-800 shrink-0" />
          </div>
          <div className="flex flex-col gap-y-1">
            <ProfileTitle>{t("company")}</ProfileTitle>
            <ProfileContent>{displayValue(profile?.company)}</ProfileContent>
          </div>
        </div>

        <div className="flex col-span-2 gap-x-3 items-center sm:col-span-1">
          <div className="bg-gray-10 p-[10px] rounded-2">
            <Icon path={mdiEmailOutline} size="20px" className="text-gray-800 shrink-0" />
          </div>
          <div className="flex flex-col gap-y-1">
            <ProfileTitle>{t("email")}</ProfileTitle>
            <ProfileContent>{displayValue(profile?.email)}</ProfileContent>
          </div>
        </div>

        <div className="flex col-span-2 gap-x-3 items-center sm:col-span-1">
          <div className="bg-gray-10 p-[10px] rounded-2">
            <Icon path={mdiPhoneOutline} size="20px" className="text-gray-800 shrink-0" />
          </div>
          <div className="flex flex-col gap-y-1">
            <ProfileTitle>{t("phone")}</ProfileTitle>
            <ProfileContent>
              {profile?.phone_number
                ? `${profile?.phone_number_country_code ? `+${profile?.phone_number_country_code} ` : ""}${profile?.phone_number}`
                : "-"}
            </ProfileContent>
          </div>
        </div>

        <div className="flex col-span-2 gap-x-3 items-center sm:col-span-1">
          <div className="bg-gray-10 p-[10px] rounded-2">
            <Icon path={mdiBadgeAccountHorizontalOutline} size="20px" className="text-gray-800 shrink-0" />
          </div>
          <div className="flex flex-col gap-y-1">
            <ProfileTitle>{t("membershipType")}</ProfileTitle>
            <ProfileContent>
              {hasMembership && membership?.plan_description?.type
                ? t(`membershipPlan_${membership.plan_description.type}`)
                : "-"}
            </ProfileContent>
          </div>
        </div>

        <div className="flex col-span-2 gap-x-3 items-center sm:col-span-1">
          <div className="bg-gray-10 p-[10px] rounded-2">
            <Icon path={mdiCalendarMonthOutline} size="20px" className="text-gray-800 shrink-0" />
          </div>
          <div className="flex flex-col gap-y-1">
            <ProfileTitle>{t("membershipPeriod")}</ProfileTitle>
            <ProfileContent>
              {hasMembership && membership?.start_date && membership?.end_date
                ? `${formatDate(membership?.start_date)} - ${formatDate(membership?.end_date)}`
                : displayValue(null)}
            </ProfileContent>
          </div>
        </div>
      </dl>
    </div>
  );
}

function ProfileTitle({ children }: PropsWithChildren) {
  return <dt className="text-gray-600 text-label3">{children}</dt>;
}

function ProfileContent({ children }: PropsWithChildren) {
  return <dd className="text-subtitle4 text-gray-1000">{children}</dd>;
}
